import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { canWrite } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Home,
  MapPin,
  Euro,
  Calendar,
  Users,
  Search,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Building2,
  WifiOff,
  Filter,
  ExternalLink,
  TrendingUp,
  RotateCcw,
  CreditCard,
  ArrowRight,
  Ban,
  ListRestart,
  CalendarPlus,
  UserPlus,
  Star,
  Zap,
  Phone,
  Unlock,
  AlertTriangle,
} from "lucide-react";

const CATEGORY_LABELS: Record<string, string> = {
  RENOVATION: "Rénovation",
  EXTENSION: "Extension",
  SALLE_DE_BAIN: "Salle de bain",
  CUISINE: "Cuisine",
  PEINTURE: "Peinture",
  CARRELAGE: "Carrelage",
  ELECTRICITE: "Électricité",
  PLOMBERIE: "Plomberie",
  CLIMATISATION: "Climatisation",
  ISOLATION: "Isolation",
  CHAUFFAGE: "Chauffage",
  MENUISERIE: "Menuiserie",
  PLATRERIE: "Plâtrerie",
  SERRURERIE: "Serrurerie",
  VITRERIE: "Vitrerie",
  TOITURE: "Toiture",
  CHARPENTE: "Charpente",
  ETANCHEITE: "Étanchéité",
  FACADE: "Façade",
  MACONNERIE: "Maçonnerie",
  TERRASSEMENT: "Terrassement",
  AMENAGEMENT_COMBLES: "Aménagement combles",
  AMENAGEMENT_EXTERIEUR: "Aménagement extérieur",
  PAYSAGISME: "Paysagisme",
  PISCINE: "Piscine",
  DOMOTIQUE: "Domotique",
  AUTRE: "Autre",
};

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }
> = {
  PUBLISHED: { label: "Publié", variant: "outline", icon: Clock },
  MATCHED: { label: "Matché", variant: "secondary", icon: CheckCircle2 },
  IN_PROGRESS: { label: "En cours", variant: "default", icon: CheckCircle2 },
  COMPLETED: { label: "Terminé", variant: "default", icon: CheckCircle2 },
  CANCELLED: { label: "Annulé", variant: "destructive", icon: AlertCircle },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || { label: status, variant: "outline" as const, icon: AlertCircle };
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function formatBudget(min?: number | null, max?: number | null): string {
  if (!min && !max) return "—";
  if (min && max) return `${min.toLocaleString("fr-FR")} – ${max.toLocaleString("fr-FR")} €`;
  if (min) return `≥ ${min.toLocaleString("fr-FR")} €`;
  if (max) return `≤ ${max.toLocaleString("fr-FR")} €`;
  return "—";
}

function formatDate(dateStr?: string | Date | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// creditPricePaid est stocké en CENTIMES (ex: 3000 = 30,00 €), cf.
// apps/server/src/routers/travauxbtpCredits.ts:744. On convertit en EUR pour l'affichage.
function formatEurFromCents(cents?: number | null): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format((cents ?? 0) / 100);
}

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

function isStaleNoUnlock(project: { unlocksCount?: number; status: string; createdAt?: string | Date | null }): boolean {
  if ((project.unlocksCount ?? 0) > 0) return false;
  if (project.status !== "PUBLISHED") return false;
  if (!project.createdAt) return false;
  return Date.now() - new Date(project.createdAt).getTime() > FORTY_EIGHT_HOURS_MS;
}

const NEXT_STATUSES: Record<string, Array<{ value: string; label: string }>> = {
  PUBLISHED: [
    { value: "MATCHED", label: "Matché" },
    { value: "IN_PROGRESS", label: "En cours" },
    { value: "CANCELLED", label: "Annulé" },
  ],
  MATCHED: [
    { value: "IN_PROGRESS", label: "En cours" },
    { value: "COMPLETED", label: "Terminé" },
    { value: "CANCELLED", label: "Annulé" },
  ],
  IN_PROGRESS: [
    { value: "COMPLETED", label: "Terminé" },
    { value: "CANCELLED", label: "Annulé" },
  ],
  COMPLETED: [],
  CANCELLED: [],
};

const LEAD_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  PROSPECT: { label: "Prospect", variant: "outline" },
  DEMO: { label: "Démo", variant: "secondary" },
  NEGOCIATION: { label: "Négociation", variant: "default" },
  GAGNE: { label: "Gagné", variant: "default" },
  PERDU: { label: "Perdu", variant: "destructive" },
};

type ViewMode = "projects" | "particuliers" | "refunds";
type StatusFilter = "ALL" | "PUBLISHED" | "MATCHED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
type OpsContactStatus = "ALL" | "NOT_CONTACTED" | "CONTACTED" | "REPLIED" | "CONVERTED";
type CategoryFilter =
  | "ALL"
  | "RENOVATION"
  | "EXTENSION"
  | "TOITURE"
  | "PLOMBERIE"
  | "ELECTRICITE"
  | "PEINTURE"
  | "MACONNERIE"
  | "AMENAGEMENT_EXTERIEUR"
  | "AUTRE";

export default function PrivateProjects() {
  const { user } = useAuth();
  const hasWrite = canWrite(user?.staffRole);
  const canSeeStaleFilter = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [searchParams] = useSearchParams();

  const [view, setView] = useState<ViewMode>("projects");

  // Particuliers tab state
  const [particuliersPage, setParticuliersPage] = useState(1);
  const [opsContactStatusFilter, setOpsContactStatusFilter] = useState<OpsContactStatus>("ALL");
  const [particuliersCategory, setParticuliersCategory] = useState("");
  const [particuliersRegion, setParticuliersRegion] = useState("");

  const { data: particuliersData, isLoading: particuliersLoading, refetch: refetchParticuliers } =
    trpc.admin.travauxbtp.listParticuliers.useQuery(
      {
        page: particuliersPage,
        limit: 20,
        opsContactStatus: opsContactStatusFilter !== "ALL" ? (opsContactStatusFilter as any) : undefined,
        category: particuliersCategory || undefined,
        region: particuliersRegion || undefined,
      },
      { enabled: view === "particuliers" }
    );

  const particuliersItems = particuliersData?.items ?? [];
  const particuliersTotal = particuliersData?.total ?? 0;
  const particuliersTotalPages = particuliersData?.totalPages ?? 1;
  const notContactedCount = particuliersItems.filter((p) => p.opsContactStatus === "NOT_CONTACTED").length;

  // Projects tab state
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("ALL");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [unlockFilter, setUnlockFilter] = useState(
    canSeeStaleFilter && searchParams.get("filter") === "stale"
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Auto-open from deep link (?id=)
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam) setSelectedProjectId(idParam);
  }, [searchParams]);

  // Match dialog state
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);

  // OPS intervention dialog state
  type OpsAction = "cancel" | "relist" | "extend" | "assign" | null;
  const [opsAction, setOpsAction] = useState<OpsAction>(null);
  const [opsReason, setOpsReason] = useState("");
  const [opsNewDeadline, setOpsNewDeadline] = useState("");
  const [opsAssignCompanyId, setOpsAssignCompanyId] = useState("");
  const [opsAssignSearch, setOpsAssignSearch] = useState("");

  // Manual refund dialog state (#1)
  const [manualRefundMatch, setManualRefundMatch] = useState<{ id: string; companyName: string } | null>(null);
  const [manualRefundReason, setManualRefundReason] = useState("");

  // Quick report dialog state (#6)
  const [quickReportOpen, setQuickReportOpen] = useState(false);
  const [quickReportForm, setQuickReportForm] = useState<{
    companyId: string;
    category: "NO_SHOW" | "PRICE_GOUGING" | "QUALITY" | "BEHAVIOR" | "SPAM" | "OTHER";
    severity: "LOW" | "MEDIUM" | "HIGH";
    description: string;
  }>({ companyId: "", category: "OTHER", severity: "MEDIUM", description: "" });

  // Create project (phone call) dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    preferCallback: false,
    title: "",
    category: "" as string,
    description: "",
    city: "",
    postalCode: "",
    budgetMin: "",
    budgetMax: "",
    deadline: "",
    isUrgent: false,
  });

  const resetCreateForm = () => {
    setCreateForm({
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      preferCallback: false,
      title: "",
      category: "",
      description: "",
      city: "",
      postalCode: "",
      budgetMin: "",
      budgetMax: "",
      deadline: "",
      isUrgent: false,
    });
  };

  const { data, isLoading, isError, refetch } = trpc.privateProjects.list.useQuery({
    page,
    limit: 20,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    category: categoryFilter !== "ALL" ? (categoryFilter as any) : undefined,
    department: departmentFilter.trim() || undefined,
  });

  const { data: projectDetail, isLoading: detailLoading, isError: detailError, refetch: refetchDetail } =
    trpc.privateProjects.getById.useQuery(
      { id: selectedProjectId! },
      { enabled: !!selectedProjectId }
    );

  // Top artisan recommendations for selected project
  const { data: topArtisans, isLoading: topArtisansLoading } =
    trpc.privateProjects.getTopArtisans.useQuery(
      { projectId: selectedProjectId!, limit: 5 },
      { enabled: !!selectedProjectId }
    );

  // All TravauxBTP leads — to show "Lead créé" badge in project table
  const { data: allTravailLeadsData } = trpc.sales.leads.list.useQuery(
    { source: "TRAVAUX_BTP" },
    { enabled: view === "projects" }
  );
  // Set of project IDs that have at least one sales lead
  const projectsWithLead = new Set(
    (allTravailLeadsData ?? [])
      .map((l: any) => l.sourcePrivateProjectId)
      .filter(Boolean)
  );

  // Sales leads associated with the selected project (source=TRAVAUX_BTP, filtered client-side)
  const projectLeads = (allTravailLeadsData ?? []).filter(
    (l: any) => l.sourcePrivateProjectId === selectedProjectId
  );

  // Companies for matching picker
  const { data: companiesData, isLoading: companiesLoading } = trpc.admin.companies.list.useQuery(
    { page: 1, limit: 100, search: companySearch || undefined },
    { enabled: matchDialogOpen }
  );

  const matchMutation = trpc.privateProjects.match.useMutation({
    onSuccess: () => {
      toast.success("Entreprises matchées avec succès");
      setMatchDialogOpen(false);
      setSelectedCompanyIds([]);
      refetch();
      if (selectedProjectId) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  // Refunds tab state
  const [refundsPage, setRefundsPage] = useState(1);
  const [refundStartDate, setRefundStartDate] = useState("");
  const [refundEndDate, setRefundEndDate] = useState("");

  const { data: refundsData, isLoading: refundsLoading, refetch: refetchRefunds } =
    trpc.admin.travauxbtp.listRefundHistory.useQuery(
      {
        page: refundsPage,
        limit: 20,
        startDate: refundStartDate ? new Date(refundStartDate).toISOString() : undefined,
        endDate: refundEndDate ? new Date(refundEndDate).toISOString() : undefined,
      },
      { enabled: view === "refunds" }
    );

  const refundItems = refundsData?.items ?? [];
  const refundTotal = refundsData?.total ?? 0;
  const refundTotalPages = refundsData?.totalPages ?? 1;
  // Somme des montants remboursés (en centimes) — formatée en EUR à l'affichage.
  const totalRefundedCents = refundItems.reduce(
    (sum, r) => sum + (r.creditPricePaid ?? 0),
    0
  );

  // Analytics period filter
  const [statsPeriod, setStatsPeriod] = useState<"7d" | "30d" | "90d" | "all">("30d");

  const { data: statsData, isLoading: statsLoading } = trpc.admin.projects.getPrivateProjectStats.useQuery(
    { period: statsPeriod },
    { enabled: view === "projects" }
  );

  const statusMutation = trpc.privateProjects.staffUpdateStatus.useMutation({
    onSuccess: () => {
      toast.success("Statut mis à jour");
      refetch();
      if (selectedProjectId) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const opsRefreshAll = () => {
    refetch();
    if (selectedProjectId) refetchDetail();
    setOpsAction(null);
    setOpsReason("");
    setOpsNewDeadline("");
    setOpsAssignCompanyId("");
    setOpsAssignSearch("");
  };

  const cancelProjectMutation = trpc.admin.travauxbtp.opsCancelProject.useMutation({
    onSuccess: () => { toast.success("Projet annulé"); opsRefreshAll(); },
    onError: (err) => toast.error(err.message),
  });
  const relistProjectMutation = trpc.admin.travauxbtp.opsRelistProject.useMutation({
    onSuccess: () => { toast.success("Projet remis en ligne"); opsRefreshAll(); },
    onError: (err) => toast.error(err.message),
  });
  const extendDeadlineMutation = trpc.admin.travauxbtp.opsExtendDeadline.useMutation({
    onSuccess: () => { toast.success("Échéance prolongée"); opsRefreshAll(); },
    onError: (err) => toast.error(err.message),
  });
  const manualAssignMutation = trpc.admin.travauxbtp.opsManualAssign.useMutation({
    onSuccess: () => { toast.success("Artisan assigné manuellement"); opsRefreshAll(); },
    onError: (err) => toast.error(err.message),
  });

  const flagSuspectMutation = trpc.admin.privateProjectQuotes.flagSuspect.useMutation({
    onSuccess: () => {
      toast.success("Devis marqué suspect (audit log enregistré)");
      opsRefreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  const refundCreditMutation = trpc.travauxbtpCredits.refundCredit.useMutation({
    onSuccess: () => {
      toast.success("Crédit remboursé au pro");
      opsRefreshAll();
    },
    onError: (err) => toast.error(err.message),
  });

  // Manual refund (#1) — admin re-credits outside standard window
  const manualRefundMutation = (trpc.admin as any).opsLeadOps.manualRefund.useMutation({
    onSuccess: () => {
      toast.success("Crédit remboursé manuellement");
      setManualRefundMatch(null);
      setManualRefundReason("");
      opsRefreshAll();
    },
    onError: (err: any) => toast.error(err?.message ?? "Erreur"),
  });

  // Quick report (#6)
  const createQuickReportMutation = (trpc.admin as any).opsLeadOps.createQuickReport.useMutation({
    onSuccess: () => {
      toast.success("Signalement créé");
      setQuickReportOpen(false);
      setQuickReportForm({ companyId: "", category: "OTHER", severity: "MEDIUM", description: "" });
      // Invalide la liste signalements pour que /ops-quick-reports soit à jour.
      (utils.admin as any).opsLeadOps?.listQuickReports?.invalidate?.();
    },
    onError: (err: any) => toast.error(err?.message ?? "Erreur"),
  });

  const createProjectMutation = trpc.admin.privateProjects.createForParticulier.useMutation({
    onSuccess: (data) => {
      toast.success("Projet créé avec succès", {
        action: {
          label: "Voir le projet",
          onClick: () => setSelectedProjectId(data.projectId),
        },
      });
      setCreateDialogOpen(false);
      resetCreateForm();
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleCreateProject = () => {
    const dept = createForm.postalCode ? createForm.postalCode.substring(0, 2) : undefined;
    createProjectMutation.mutate({
      email: createForm.email,
      firstName: createForm.firstName || undefined,
      lastName: createForm.lastName || undefined,
      phone: createForm.phone || undefined,
      preferCallback: createForm.preferCallback,
      title: createForm.title,
      category: createForm.category as any,
      description: createForm.description || undefined,
      city: createForm.city || undefined,
      postalCode: createForm.postalCode || undefined,
      department: dept,
      budgetMin: createForm.budgetMin ? Number(createForm.budgetMin) : undefined,
      budgetMax: createForm.budgetMax ? Number(createForm.budgetMax) : undefined,
      deadline: createForm.deadline ? new Date(createForm.deadline).toISOString() : undefined,
      isUrgent: createForm.isUrgent,
    });
  };

  const { data: assignCompanies } = trpc.admin.companies.list.useQuery(
    { page: 1, limit: 50, search: opsAssignSearch || undefined },
    { enabled: opsAction === "assign" }
  );

  const opsActionPending =
    cancelProjectMutation.isPending ||
    relistProjectMutation.isPending ||
    extendDeadlineMutation.isPending ||
    manualAssignMutation.isPending;

  const handleOpsAction = () => {
    if (!selectedProjectId) return;
    switch (opsAction) {
      case "cancel":
        cancelProjectMutation.mutate({ projectId: selectedProjectId, reason: opsReason });
        break;
      case "relist":
        relistProjectMutation.mutate({ projectId: selectedProjectId, reason: opsReason || undefined });
        break;
      case "extend":
        if (!opsNewDeadline) return;
        extendDeadlineMutation.mutate({
          projectId: selectedProjectId,
          newDeadline: new Date(opsNewDeadline),
          reason: opsReason || undefined,
        });
        break;
      case "assign":
        if (!opsAssignCompanyId) return;
        manualAssignMutation.mutate({
          projectId: selectedProjectId,
          companyId: opsAssignCompanyId,
          reason: opsReason || undefined,
        });
        break;
    }
  };

  const handleOpenDetail = (id: string) => {
    setSelectedProjectId(id);
  };

  const handleCloseDetail = () => {
    setSelectedProjectId(null);
  };

  const handleOpenMatch = () => {
    setSelectedCompanyIds([]);
    setCompanySearch("");
    setMatchDialogOpen(true);
  };

  const handleToggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  const handleMatch = () => {
    if (!selectedProjectId || selectedCompanyIds.length === 0) return;
    matchMutation.mutate({ projectId: selectedProjectId, companyIds: selectedCompanyIds });
  };

  const filteredItems = canSeeStaleFilter && unlockFilter
    ? (data?.items ?? []).filter((p) => isStaleNoUnlock(p))
    : (data?.items ?? []);
  const staleCount = canSeeStaleFilter
    ? (data?.items ?? []).filter((p) => isStaleNoUnlock(p)).length
    : 0;
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Home className="h-6 w-6" />
              Projets TravauxBTP
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Projets de particuliers à qualifier et matcher avec des entreprises BTP
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasWrite && (
              <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                <Phone className="h-4 w-4 mr-2" />
                + Nouveau projet (téléphone)
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => (view === "projects" ? refetch() : refetchRefunds())}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 border-b border-border">
          <button
            type="button"
            onClick={() => setView("projects")}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              view === "projects"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Home className="h-3.5 w-3.5 inline mr-1.5" />
            Projets
          </button>
          <button
            type="button"
            onClick={() => setView("particuliers")}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              view === "particuliers"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="h-3.5 w-3.5 inline mr-1.5" />
            Outreach particuliers
            {particuliersTotal > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-muted text-xs font-medium">
                {particuliersTotal}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setView("refunds")}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              view === "refunds"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <RotateCcw className="h-3.5 w-3.5 inline mr-1.5" />
            Remboursements garantie
            {refundTotal > 0 && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-muted text-xs font-medium">
                {refundTotal}
              </span>
            )}
          </button>
        </div>

        {/* ── Projects view ── */}
        {view === "projects" && (<>

        {/* Analytics section */}
        <div className="space-y-4">
          {/* Period selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground font-medium">Période :</span>
            {(["7d", "30d", "90d", "all"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setStatsPeriod(p)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  statsPeriod === p
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {p === "7d" ? "7 jours" : p === "30d" ? "30 jours" : p === "90d" ? "90 jours" : "Tout"}
              </button>
            ))}
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total projets</CardTitle>
                <Home className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading ? "—" : (statsData?.total ?? 0)}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Taux matching</CardTitle>
                <TrendingUp className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">
                  {statsLoading ? "—" : `${statsData?.matchingRate ?? 0}%`}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Budget moyen</CardTitle>
                <Euro className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {statsLoading
                    ? "—"
                    : statsData?.avgBudget
                    ? `${statsData.avgBudget.toLocaleString("fr-FR")} €`
                    : "—"}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Taux complétion</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-success" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">
                  {statsLoading ? "—" : `${statsData?.completionRate ?? 0}%`}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Mini-funnel */}
          {!statsLoading && statsData && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Funnel projets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { key: "PUBLISHED", label: "Publié", count: statsData.byStatus.PUBLISHED, color: "bg-warning/10 text-warning border-warning/20" },
                    { key: "MATCHED", label: "Matché", count: statsData.byStatus.MATCHED, color: "bg-primary/10 text-primary border-primary/20" },
                    { key: "IN_PROGRESS", label: "En cours", count: statsData.byStatus.IN_PROGRESS, color: "bg-primary/10 text-primary border-primary/20" },
                    { key: "COMPLETED", label: "Terminé", count: statsData.byStatus.COMPLETED, color: "bg-success/10 text-success border-success/20" },
                  ].map((step, idx, arr) => {
                    const pct = statsData.total > 0
                      ? Math.round((step.count / statsData.total) * 100)
                      : 0;
                    return (
                      <div key={step.key} className="flex items-center gap-2">
                        <div className={`flex flex-col items-center px-4 py-2 rounded-lg border text-center min-w-[90px] ${step.color}`}>
                          <span className="text-xl font-bold">{step.count}</span>
                          <span className="text-xs font-medium">{step.label}</span>
                          <span className="text-xs opacity-70">{pct}%</span>
                        </div>
                        {idx < arr.length - 1 && (
                          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                  {statsData.byStatus.CANCELLED > 0 && (
                    <div className="ml-4 flex flex-col items-center px-4 py-2 rounded-lg border text-center min-w-[90px] bg-destructive/10 text-destructive border-destructive/20">
                      <span className="text-xl font-bold">{statsData.byStatus.CANCELLED}</span>
                      <span className="text-xs font-medium">Annulé</span>
                      <span className="text-xs opacity-70">
                        {Math.round((statsData.byStatus.CANCELLED / statsData.total) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setPage(1); }}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tous les statuts</SelectItem>
                  <SelectItem value="PUBLISHED">Publié</SelectItem>
                  <SelectItem value="MATCHED">Matché</SelectItem>
                  <SelectItem value="IN_PROGRESS">En cours</SelectItem>
                  <SelectItem value="COMPLETED">Terminé</SelectItem>
                  <SelectItem value="CANCELLED">Annulé</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v as CategoryFilter); setPage(1); }}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Toutes catégories</SelectItem>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-36">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Département"
                  value={departmentFilter}
                  onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}
                  className="pl-8 h-9 text-sm"
                />
              </div>
              {canSeeStaleFilter && (
                <Button
                  variant={unlockFilter ? "default" : "outline"}
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setUnlockFilter((v) => !v)}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Sans activité 48h+
                  {staleCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-medium">
                      {staleCount}
                    </span>
                  )}
                </Button>
              )}
              {data && (
                <span className="text-sm text-muted-foreground ml-auto">
                  {data.total} projet{data.total !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Liste des projets</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                <WifiOff className="h-8 w-8" />
                <p>Impossible de charger les projets</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            ) : !data?.items?.length ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                <Home className="h-8 w-8" />
                <p>Aucun projet trouvé</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 text-success" />
                <p>Aucun projet sans unlock depuis 48h sur cette page</p>
                <Button variant="outline" size="sm" onClick={() => setUnlockFilter(false)}>
                  Retirer le filtre
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Projet</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Localisation</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Unlocks</TableHead>
                    <TableHead>Entreprises</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((project) => (
                    <TableRow key={project.id} className={`cursor-pointer hover:bg-muted/50 ${canSeeStaleFilter && isStaleNoUnlock(project) ? "bg-destructive/5" : ""}`}>
                      <TableCell className="font-medium max-w-48 truncate">{project.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{CATEGORY_LABELS[project.category] || project.category}</Badge>
                      </TableCell>
                      <TableCell>
                        {project.city ? (
                          <span className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            {project.city}
                            {project.department ? ` (${project.department})` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatBudget(project.budgetMin, project.budgetMax)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <StatusBadge status={project.status} />
                          {projectsWithLead.has(project.id) && (
                            <Badge variant="secondary" className="text-xs gap-1 bg-success/10 text-success border-success/20">
                              <TrendingUp className="h-2.5 w-2.5" />
                              Lead créé
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="flex items-center gap-1 text-sm">
                            <Unlock className="h-3 w-3 text-muted-foreground" />
                            {(project as any).unlocksCount ?? 0}/{(project as any).maxUnlocks ?? 3}
                          </span>
                          {canSeeStaleFilter && isStaleNoUnlock(project) && (
                            <Badge variant="destructive" className="text-xs gap-1 py-0">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              48h+
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm">
                          <Building2 className="h-3 w-3 text-muted-foreground" />
                          {project.matchedCompanyCount ?? 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(project.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDetail(project.id)}
                        >
                          Voir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        </>)}

        {/* ── Particuliers view ── */}
        {view === "particuliers" && (
          <div className="space-y-4">
            {/* Counter non contactés */}
            {notContactedCount > 0 && (
              <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 text-warning shrink-0" />
                <span className="text-warning font-medium">
                  {notContactedCount} particulier{notContactedCount > 1 ? "s" : ""} non contacté{notContactedCount > 1 ? "s" : ""} sur cette page
                </span>
              </div>
            )}

            {/* Filters */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-wrap gap-3 items-center">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select
                    value={opsContactStatusFilter}
                    onValueChange={(v) => { setOpsContactStatusFilter(v as OpsContactStatus); setParticuliersPage(1); }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Statut contact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Tous les statuts</SelectItem>
                      <SelectItem value="NOT_CONTACTED">Non contacté</SelectItem>
                      <SelectItem value="CONTACTED">Contacté</SelectItem>
                      <SelectItem value="REPLIED">A répondu</SelectItem>
                      <SelectItem value="CONVERTED">Converti</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={particuliersCategory || "ALL"}
                    onValueChange={(v) => { setParticuliersCategory(v === "ALL" ? "" : v); setParticuliersPage(1); }}
                  >
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Toutes catégories</SelectItem>
                      {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative w-36">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Région (ex: 75)"
                      value={particuliersRegion}
                      onChange={(e) => { setParticuliersRegion(e.target.value); setParticuliersPage(1); }}
                      className="pl-8 h-9 text-sm"
                    />
                  </div>
                  <span className="text-sm text-muted-foreground ml-auto">
                    {particuliersTotal} particulier{particuliersTotal !== 1 ? "s" : ""}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => refetchParticuliers()}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Liste des particuliers</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {particuliersLoading ? (
                  <div className="p-6 space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-10 w-full rounded bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : particuliersItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                    <Users className="h-8 w-8" />
                    <p>Aucun particulier trouvé</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Particulier</TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Localisation</TableHead>
                        <TableHead>Budget</TableHead>
                        <TableHead>Statut contact</TableHead>
                        <TableHead>Déposé le</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {particuliersItems.map((p) => {
                        const contactCfg = {
                          NOT_CONTACTED: { label: "Non contacté", cls: "bg-muted text-muted-foreground" },
                          CONTACTED: { label: "Contacté", cls: "bg-warning/10 text-warning" },
                          REPLIED: { label: "A répondu", cls: "bg-primary/10 text-primary" },
                          CONVERTED: { label: "Converti", cls: "bg-success/10 text-success" },
                        }[p.opsContactStatus] ?? { label: p.opsContactStatus, cls: "bg-muted text-muted-foreground" };
                        return (
                          <TableRow
                            key={p.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/particuliers/${p.id}`)}
                          >
                            <TableCell className="text-sm">
                              <div className="font-medium">
                                {p.createdBy.firstName} {p.createdBy.lastName}
                              </div>
                              <div className="text-xs text-muted-foreground">{p.createdBy.email}</div>
                            </TableCell>
                            <TableCell className="max-w-40 truncate font-medium text-sm">{p.title}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{CATEGORY_LABELS[p.category] ?? p.category}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {[p.city, p.department].filter(Boolean).join(", ") || "—"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatBudget(p.budgetMin, p.budgetMax)}
                            </TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${contactCfg.cls}`}>
                                {contactCfg.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(p.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigate(`/particuliers/${p.id}`); }}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {particuliersTotalPages > 1 && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setParticuliersPage((p) => Math.max(1, p - 1))}
                  disabled={particuliersPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {particuliersPage} / {particuliersTotalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setParticuliersPage((p) => Math.min(particuliersTotalPages, p + 1))}
                  disabled={particuliersPage === particuliersTotalPages}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── Refunds view ── */}
        {view === "refunds" && (
          <div className="space-y-4">
            {/* KPI + Filters row */}
            <div className="flex flex-wrap items-end gap-4">
              {/* KPI */}
              <Card className="flex-1 min-w-48">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-warning/10">
                      <CreditCard className="h-5 w-5 text-warning" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Remboursements (page)</div>
                      <div className="text-2xl font-bold">{formatEurFromCents(totalRefundedCents)}</div>
                      <div className="text-xs text-muted-foreground">remboursés · {refundTotal} match{refundTotal > 1 ? "s" : ""}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Date filters */}
              <div className="flex items-end gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Depuis</label>
                  <input
                    type="date"
                    value={refundStartDate}
                    onChange={(e) => { setRefundStartDate(e.target.value); setRefundsPage(1); }}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Jusqu'au</label>
                  <input
                    type="date"
                    value={refundEndDate}
                    onChange={(e) => { setRefundEndDate(e.target.value); setRefundsPage(1); }}
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  />
                </div>
                {(refundStartDate || refundEndDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setRefundStartDate(""); setRefundEndDate(""); setRefundsPage(1); }}
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            </div>

            {/* Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  Historique des remboursements garantie lead
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {refundsLoading ? (
                  <div className="p-6 space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-10 w-full rounded bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : refundItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                    <RotateCcw className="h-8 w-8" />
                    <p>Aucun remboursement trouvé pour cette période</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pro (entreprise)</TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead>Date déblocage</TableHead>
                        <TableHead>Date remboursement</TableHead>
                        <TableHead>Montant remboursé</TableHead>
                        <TableHead>Raison</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {refundItems.map((r) => (
                        <TableRow key={r.matchId}>
                          <TableCell className="font-medium text-sm">
                            {r.companyName ?? <span className="text-muted-foreground italic">—</span>}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{r.projectTitle ?? "—"}</div>
                            {r.projectCity && (
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {r.projectCity}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(r.unlockedAt)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(r.refundedAt)}
                          </TableCell>
                          <TableCell>
                            {r.creditPricePaid != null ? (
                              <Badge variant="secondary">
                                <CreditCard className="h-3 w-3 mr-1" />
                                {formatEurFromCents(r.creditPricePaid)}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm max-w-64">
                            {r.refundReason ? (
                              <span className="text-muted-foreground line-clamp-2">{r.refundReason}</span>
                            ) : (
                              <span className="text-muted-foreground italic">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Refunds pagination */}
            {refundTotalPages > 1 && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundsPage((p) => Math.max(1, p - 1))}
                  disabled={refundsPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {refundsPage} / {refundTotalPages} — {refundTotal} remboursement{refundTotal > 1 ? "s" : ""}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundsPage((p) => Math.min(refundTotalPages, p + 1))}
                  disabled={refundsPage === refundTotalPages}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Project Detail Dialog */}
      <Dialog open={!!selectedProjectId} onOpenChange={(open) => { if (!open) handleCloseDetail(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="h-5 w-5" />
              Détail du projet
            </DialogTitle>
            <DialogDescription>
              Informations complètes et suivi du matching
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="space-y-3 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : detailError ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-muted-foreground">
              <WifiOff className="h-8 w-8" />
              <p>Impossible de charger le projet</p>
              <Button variant="outline" size="sm" onClick={() => refetchDetail()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </div>
          ) : projectDetail ? (
            <div className="space-y-5 py-2">
              {/* Status + Category */}
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={projectDetail.status} />
                <Badge variant="outline">{CATEGORY_LABELS[projectDetail.category] || projectDetail.category}</Badge>
                {hasWrite && NEXT_STATUSES[projectDetail.status]?.length > 0 && (
                  <Select
                    value="__none__"
                    onValueChange={(v) => statusMutation.mutate({ id: projectDetail.id, status: v as any })}
                    disabled={statusMutation.isPending}
                  >
                    <SelectTrigger className="h-7 w-44 text-xs">
                      <SelectValue placeholder="Changer le statut…" />
                    </SelectTrigger>
                    <SelectContent>
                      {NEXT_STATUSES[projectDetail.status].map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* OPS Intervention Actions */}
              {hasWrite && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {projectDetail.status !== "CANCELLED" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => { setOpsAction("cancel"); setOpsReason(""); }}
                    >
                      <Ban className="h-3.5 w-3.5 mr-1" />
                      Annuler
                    </Button>
                  )}
                  {(projectDetail.status === "CANCELLED" || projectDetail.status === "COMPLETED") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setOpsAction("relist"); setOpsReason(""); }}
                    >
                      <ListRestart className="h-3.5 w-3.5 mr-1" />
                      Remettre en ligne
                    </Button>
                  )}
                  {projectDetail.status !== "CANCELLED" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setOpsAction("extend"); setOpsReason(""); setOpsNewDeadline(""); }}
                    >
                      <CalendarPlus className="h-3.5 w-3.5 mr-1" />
                      Prolonger délai
                    </Button>
                  )}
                  {(projectDetail.status === "PUBLISHED" || projectDetail.status === "MATCHED") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setOpsAction("assign"); setOpsReason(""); setOpsAssignCompanyId(""); setOpsAssignSearch(""); }}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      Assigner artisan
                    </Button>
                  )}
                </div>
              )}

              {/* Title + Description */}
              <div>
                <h2 className="font-semibold text-lg">{projectDetail.title}</h2>
                {projectDetail.description && (
                  <p className="text-sm text-muted-foreground mt-1">{projectDetail.description}</p>
                )}
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Localisation</div>
                    <div className="text-muted-foreground">
                      {[projectDetail.city, projectDetail.postalCode, projectDetail.department]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Euro className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Budget</div>
                    <div className="text-muted-foreground">
                      {formatBudget(projectDetail.budgetMin, projectDetail.budgetMax)}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Échéance souhaitée</div>
                    <div className="text-muted-foreground">{formatDate(projectDetail.deadline)}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium">Créé par</div>
                    <div className="text-muted-foreground">
                      {projectDetail.CreatedBy
                        ? `${projectDetail.CreatedBy.firstName || ""} ${projectDetail.CreatedBy.lastName || ""}`.trim() ||
                          projectDetail.CreatedBy.email
                        : "—"}
                    </div>
                    {projectDetail.CreatedBy?.phone && (
                      <div className="text-muted-foreground">{projectDetail.CreatedBy.phone}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Artisan Recommendations (AI Matching) */}
              {hasWrite && (
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Suggestions algorithme (non-matché — gratuit)
                  </h3>
                  {topArtisansLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : topArtisans && topArtisans.length > 0 ? (
                    <div className="space-y-2">
                      {topArtisans.map((artisan: any, idx: number) => (
                        <div
                          key={artisan.companyId}
                          className="border rounded-lg p-3 text-sm space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-muted-foreground">#{idx + 1}</span>
                              <span className="font-medium">{artisan.companyName}</span>
                              {artisan.avgRating != null && (
                                <span className="flex items-center gap-0.5 text-xs text-amber-600">
                                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                                  {artisan.avgRating.toFixed(1)} ({artisan.reviewCount})
                                </span>
                              )}
                            </div>
                            <Badge
                              variant={artisan.score >= 70 ? "default" : artisan.score >= 40 ? "secondary" : "outline"}
                            >
                              Score: {artisan.score}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-4 gap-1 text-xs text-muted-foreground">
                            <span>Catégorie: {artisan.breakdown.categoryMatch}</span>
                            <span>Zone: {artisan.breakdown.zoneMatch}</span>
                            <span>Note: {artisan.breakdown.rating}</span>
                            <span>Dispo: {artisan.breakdown.availability}</span>
                            {artisan.breakdown.urgencyBonus > 0 && (
                              <span className="text-green-600">+Urgence: {artisan.breakdown.urgencyBonus}</span>
                            )}
                            {artisan.breakdown.responseRateBonus > 0 && (
                              <span className="text-green-600">+Réactivité: {artisan.breakdown.responseRateBonus}</span>
                            )}
                            {artisan.breakdown.rotationPenalty < 0 && (
                              <span className="text-red-500">Rotation: {artisan.breakdown.rotationPenalty}</span>
                            )}
                            {artisan.breakdown.budgetMatch > 0 && (
                              <span className="text-blue-600">Budget: {artisan.breakdown.budgetMatch}</span>
                            )}
                          </div>
                          {artisan.specialties?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {artisan.specialties.slice(0, 4).map((s: string) => (
                                <Badge key={s} variant="outline" className="text-xs py-0">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune recommandation disponible</p>
                  )}
                </div>
              )}

              {/* Matched companies — suivi artisans */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Entreprises matchées ({projectDetail.MatchedCompanies?.length ?? 0})
                  </h3>
                  {hasWrite && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setQuickReportForm({ companyId: "", category: "OTHER", severity: "MEDIUM", description: "" });
                          setQuickReportOpen(true);
                        }}
                      >
                        <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                        Signaler
                      </Button>
                      <Button size="sm" onClick={handleOpenMatch}>
                        + Matcher des entreprises
                      </Button>
                    </div>
                  )}
                </div>
                {projectDetail.MatchedCompanies?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entreprise</TableHead>
                        <TableHead>Matché</TableHead>
                        <TableHead>Vu</TableHead>
                        <TableHead>Débloqué</TableHead>
                        <TableHead className="text-center">Devis</TableHead>
                        <TableHead className="text-center">Msgs</TableHead>
                        <TableHead className="text-center">Appels</TableHead>
                        {hasWrite && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectDetail.MatchedCompanies.map((m: any) => {
                        const quotesForCompany = (projectDetail.Quotes ?? []).filter(
                          (q: any) => q.companyId === m.companyId,
                        ).length;
                        const messageCount =
                          projectDetail.messageCountsByCompanyId?.[m.companyId] ?? 0;
                        return (
                          <TableRow key={m.id}>
                            <TableCell className="font-medium text-sm">
                              {m.Company?.name || m.companyId}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(m.matchedAt)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {m.viewedAt ? (
                                <span className="text-success">{formatDate(m.viewedAt)}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {m.unlockedAt ? (
                                <span className="text-success">
                                  {formatDate(m.unlockedAt)}
                                  {m.creditPricePaid ? ` · ${m.creditPricePaid / 100}€` : ""}
                                  {m.refundedAt ? " (remb.)" : ""}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center text-sm tabular-nums">
                              {quotesForCompany || <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            <TableCell className="text-center text-sm tabular-nums">
                              {messageCount || <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            <TableCell className="text-center text-sm tabular-nums">
                              {m.callCount || <span className="text-muted-foreground">0</span>}
                            </TableCell>
                            {hasWrite && (
                              <TableCell className="text-right">
                                {m.unlockedAt && !m.refundedAt && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setManualRefundMatch({
                                        id: m.id,
                                        companyName: m.Company?.name ?? m.companyId,
                                      })
                                    }
                                  >
                                    Refund manuel
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucune entreprise matchée</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  « Débloqué » = l'artisan a payé un crédit pour voir les coordonnées du particulier. « Matché » seul = recommandation algorithmique gratuite.
                </p>
              </div>

              {/* Quotes */}
              {projectDetail.Quotes?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Devis reçus ({projectDetail.Quotes.length})</h3>
                  <div className="space-y-3">
                    {projectDetail.Quotes.map((q: any) => {
                      // find the match for this pro on this project (for refund)
                      const proMatch = projectDetail.MatchedCompanies?.find((m: any) => m.companyId === q.companyId);
                      const validUntil = q.validUntil ? new Date(q.validUntil) : null;
                      const now = new Date();
                      const isExpired = validUntil && validUntil < now;
                      const daysLeft = validUntil
                        ? Math.ceil((validUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                        : null;
                      const createdAt = q.createdAt ? new Date(q.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
                      const viewedAt = q.viewedAt ? new Date(q.viewedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
                      return (
                        <div key={q.id} className="rounded-lg border p-3 text-sm space-y-2">
                          {/* Header: company + amount + status */}
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="font-medium">{q.Company?.name ?? "—"}</div>
                              {createdAt && (
                                <div className="text-xs text-muted-foreground">Soumis le {createdAt}</div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{q.amount?.toLocaleString("fr-FR")} €</div>
                              <Badge variant={q.status === "ACCEPTED" ? "default" : q.status === "REJECTED" ? "destructive" : "outline"} className="mt-0.5">
                                {q.status}
                              </Badge>
                            </div>
                          </div>

                          {/* Description */}
                          {q.description && (
                            <div className="rounded bg-muted/40 p-2 text-xs whitespace-pre-wrap break-words">
                              {q.description}
                            </div>
                          )}

                          {/* Meta line */}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            {q.estimatedDuration && (
                              <span>Durée : {q.estimatedDuration}</span>
                            )}
                            {validUntil && (
                              <span className={isExpired ? "text-destructive font-medium" : (daysLeft !== null && daysLeft <= 3 ? "text-warning font-medium" : "")}>
                                {isExpired
                                  ? `Expiré le ${validUntil.toLocaleDateString("fr-FR")}`
                                  : `Valide jusqu'au ${validUntil.toLocaleDateString("fr-FR")} (${daysLeft}j)`}
                              </span>
                            )}
                            <span>
                              {viewedAt ? `Vu par le particulier le ${viewedAt}` : "Pas encore vu par le particulier"}
                            </span>
                          </div>

                          {/* Rejection reason */}
                          {q.status === "REJECTED" && q.rejectionReason && (
                            <div className="rounded bg-destructive/10 text-destructive text-xs p-2">
                              <span className="font-medium">Motif refus :</span> {q.rejectionReason}
                            </div>
                          )}

                          {/* PDF document */}
                          {q.documentUrl && (
                            <div className="flex items-center gap-2 pt-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => window.open(q.documentUrl, "_blank", "noopener")}
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Ouvrir le PDF{q.documentFileName ? ` — ${q.documentFileName}` : ""}
                              </Button>
                            </div>
                          )}

                          {/* Staff moderation actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                const reason = window.prompt("Raison (visible dans l'audit log uniquement) :");
                                if (!reason || !reason.trim()) return;
                                flagSuspectMutation.mutate({ quoteId: q.id, reason: reason.trim() });
                              }}
                              disabled={flagSuspectMutation.isPending}
                            >
                              🚩 Marquer suspect
                            </Button>
                            {proMatch?.unlockedAt && !proMatch?.refundedAt && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-destructive hover:text-destructive"
                                onClick={() => {
                                  const reason = window.prompt(
                                    "Motif du remboursement (devis non conforme, lead non qualifié, etc.) :"
                                  );
                                  if (!reason || !reason.trim()) return;
                                  refundCreditMutation.mutate({
                                    matchId: proMatch.id,
                                    companyId: q.companyId,
                                    reason: reason.trim(),
                                  });
                                }}
                                disabled={refundCreditMutation.isPending}
                              >
                                💸 Forcer remboursement crédit
                              </Button>
                            )}
                            {proMatch?.refundedAt && (
                              <span className="text-xs text-muted-foreground italic">
                                Crédit déjà remboursé le {new Date(proMatch.refundedAt).toLocaleDateString("fr-FR")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sales Leads associés */}
              <div>
                <h3 className="font-medium mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Leads Sales associés ({projectLeads.length})
                </h3>
                {projectLeads.length > 0 ? (
                  <div className="space-y-2">
                    {projectLeads.map((lead) => {
                      const statusCfg = LEAD_STATUS_CONFIG[lead.status] ?? { label: lead.status, variant: "outline" as const };
                      return (
                        <div key={lead.id} className="flex items-center gap-3 p-2 rounded-md border text-sm">
                          <span className="font-medium flex-1 truncate">{lead.name}</span>
                          <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() => navigate(`/sales/leads/${lead.id}`)}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Voir
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun lead généré</p>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDetail}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match Companies Dialog */}
      <Dialog open={matchDialogOpen} onOpenChange={setMatchDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Matcher des entreprises</DialogTitle>
            <DialogDescription>
              Sélectionnez les entreprises BTP à proposer pour ce projet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une entreprise..."
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {companiesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : companiesData?.items?.length ? (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {companiesData.items.map((company: any) => {
                  const isSelected = selectedCompanyIds.includes(company.id);
                  const alreadyMatched = projectDetail?.MatchedCompanies?.some(
                    (m: any) => m.companyId === company.id
                  );
                  return (
                    <button
                      key={company.id}
                      type="button"
                      disabled={alreadyMatched}
                      onClick={() => handleToggleCompany(company.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                        alreadyMatched
                          ? "opacity-50 cursor-not-allowed bg-muted"
                          : isSelected
                          ? "bg-primary/10 border border-primary"
                          : "hover:bg-muted border border-transparent"
                      }`}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium flex-1">{company.name}</span>
                      {alreadyMatched && (
                        <Badge variant="secondary" className="text-xs">Déjà matché</Badge>
                      )}
                      {isSelected && !alreadyMatched && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-muted-foreground">
                Aucune entreprise trouvée
              </div>
            )}

            {selectedCompanyIds.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedCompanyIds.length} entreprise{selectedCompanyIds.length > 1 ? "s" : ""} sélectionnée{selectedCompanyIds.length > 1 ? "s" : ""}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleMatch}
              disabled={selectedCompanyIds.length === 0 || matchMutation.isPending}
            >
              {matchMutation.isPending ? "Matching..." : "Confirmer le matching"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project (Phone Call) Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) { setCreateDialogOpen(false); resetCreateForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Nouveau projet (appel téléphonique)
            </DialogTitle>
            <DialogDescription>
              Créer un projet pour un particulier qui appelle par téléphone
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Informations du particulier</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="create-email">Email *</Label>
                  <Input
                    id="create-email"
                    type="email"
                    placeholder="email@exemple.com"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-phone">Téléphone</Label>
                  <Input
                    id="create-phone"
                    placeholder="06 12 34 56 78"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-firstname">Prénom</Label>
                  <Input
                    id="create-firstname"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-lastname">Nom</Label>
                  <Input
                    id="create-lastname"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create-callback"
                  checked={createForm.preferCallback}
                  onChange={(e) => setCreateForm((f) => ({ ...f, preferCallback: e.target.checked }))}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="create-callback" className="text-sm font-normal">
                  Le particulier préfère être rappelé
                </Label>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-sm">Informations du projet</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="create-title">Titre du projet *</Label>
                  <Input
                    id="create-title"
                    placeholder="Ex: Rénovation salle de bain"
                    value={createForm.title}
                    onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Catégorie *</Label>
                  <Select
                    value={createForm.category}
                    onValueChange={(v) => setCreateForm((f) => ({ ...f, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-deadline">Date souhaitée</Label>
                  <Input
                    id="create-deadline"
                    type="date"
                    value={createForm.deadline}
                    onChange={(e) => setCreateForm((f) => ({ ...f, deadline: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="create-description">Description</Label>
                  <Textarea
                    id="create-description"
                    placeholder="Décrivez le projet en détail (min. 50 caractères)..."
                    value={createForm.description}
                    onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-city">Ville</Label>
                  <Input
                    id="create-city"
                    value={createForm.city}
                    onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-postalcode">Code postal</Label>
                  <Input
                    id="create-postalcode"
                    placeholder="75001"
                    value={createForm.postalCode}
                    onChange={(e) => setCreateForm((f) => ({ ...f, postalCode: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-budgetmin">Budget min (€)</Label>
                  <Input
                    id="create-budgetmin"
                    type="number"
                    placeholder="0"
                    value={createForm.budgetMin}
                    onChange={(e) => setCreateForm((f) => ({ ...f, budgetMin: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="create-budgetmax">Budget max (€)</Label>
                  <Input
                    id="create-budgetmax"
                    type="number"
                    placeholder="0"
                    value={createForm.budgetMax}
                    onChange={(e) => setCreateForm((f) => ({ ...f, budgetMax: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="create-urgent"
                  checked={createForm.isUrgent}
                  onChange={(e) => setCreateForm((f) => ({ ...f, isUrgent: e.target.checked }))}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="create-urgent" className="text-sm font-normal">
                  Projet urgent (notifie les artisans immédiatement)
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetCreateForm(); }} disabled={createProjectMutation.isPending}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={
                createProjectMutation.isPending ||
                !createForm.email ||
                !createForm.title ||
                !createForm.category
              }
            >
              {createProjectMutation.isPending ? "Création..." : "Créer le projet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OPS Intervention Action Dialog */}
      <Dialog open={!!opsAction} onOpenChange={(open) => { if (!open) setOpsAction(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {opsAction === "cancel" && "Annuler le projet"}
              {opsAction === "relist" && "Remettre en ligne"}
              {opsAction === "extend" && "Prolonger le délai"}
              {opsAction === "assign" && "Assigner un artisan manuellement"}
            </DialogTitle>
            <DialogDescription>
              {opsAction === "cancel" && "Le projet sera annulé et le particulier sera notifié."}
              {opsAction === "relist" && "Le projet sera republié pour un nouveau cycle de matching."}
              {opsAction === "extend" && "Définissez la nouvelle date limite pour ce projet."}
              {opsAction === "assign" && "Sélectionnez l'entreprise artisan à assigner à ce projet."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {opsAction === "extend" && (
              <div className="space-y-2">
                <Label htmlFor="ops-deadline">Nouvelle échéance</Label>
                <Input
                  id="ops-deadline"
                  type="date"
                  value={opsNewDeadline}
                  onChange={(e) => setOpsNewDeadline(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
            )}

            {opsAction === "assign" && (
              <div className="space-y-2">
                <Label>Entreprise artisan</Label>
                <Input
                  placeholder="Rechercher une entreprise..."
                  value={opsAssignSearch}
                  onChange={(e) => setOpsAssignSearch(e.target.value)}
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {assignCompanies?.items?.map((company: any) => (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => setOpsAssignCompanyId(company.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                        opsAssignCompanyId === company.id
                          ? "bg-primary/10 border border-primary"
                          : "hover:bg-muted border border-transparent"
                      }`}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="font-medium flex-1">{company.name}</span>
                      {opsAssignCompanyId === company.id && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ops-reason">
                Raison {opsAction === "cancel" ? "(obligatoire)" : "(optionnel)"}
              </Label>
              <Textarea
                id="ops-reason"
                placeholder="Expliquez la raison de cette action..."
                value={opsReason}
                onChange={(e) => setOpsReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpsAction(null)} disabled={opsActionPending}>
              Annuler
            </Button>
            <Button
              variant={opsAction === "cancel" ? "destructive" : "default"}
              onClick={handleOpsAction}
              disabled={
                opsActionPending ||
                (opsAction === "cancel" && opsReason.trim().length < 5) ||
                (opsAction === "extend" && !opsNewDeadline) ||
                (opsAction === "assign" && !opsAssignCompanyId)
              }
            >
              {opsActionPending ? "En cours..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual refund dialog (#1) */}
      <Dialog
        open={!!manualRefundMatch}
        onOpenChange={(open) => {
          if (!open) {
            setManualRefundMatch(null);
            setManualRefundReason("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remboursement manuel</DialogTitle>
            <DialogDescription>
              Re-créditer {manualRefundMatch?.companyName ?? "l'entreprise"} pour ce match. Cette
              action est tracée dans l'audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="manual-refund-reason">Raison du remboursement</Label>
            <Textarea
              id="manual-refund-reason"
              placeholder="Expliquez pourquoi vous remboursez ce crédit..."
              value={manualRefundReason}
              onChange={(e) => setManualRefundReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setManualRefundMatch(null);
                setManualRefundReason("");
              }}
              disabled={manualRefundMutation.isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!manualRefundMatch) return;
                manualRefundMutation.mutate({
                  matchId: manualRefundMatch.id,
                  reason: manualRefundReason.trim(),
                });
              }}
              disabled={
                manualRefundMutation.isLoading || manualRefundReason.trim().length < 3
              }
            >
              {manualRefundMutation.isLoading ? "En cours..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick report dialog (#6) */}
      <Dialog
        open={quickReportOpen}
        onOpenChange={(open) => {
          if (!open) setQuickReportOpen(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Signaler un problème</DialogTitle>
            <DialogDescription>
              Crée un signalement OPS attaché à ce projet. Optionnel : cibler un artisan précis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Artisan ciblé (optionnel)</Label>
              <Select
                value={quickReportForm.companyId || "__none"}
                onValueChange={(v) =>
                  setQuickReportForm((f) => ({ ...f, companyId: v === "__none" ? "" : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Aucun (projet seul)</SelectItem>
                  {projectDetail?.MatchedCompanies?.map((m: any) => (
                    <SelectItem key={m.companyId} value={m.companyId}>
                      {m.Company?.name ?? m.companyId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Catégorie</Label>
              <Select
                value={quickReportForm.category}
                onValueChange={(v) =>
                  setQuickReportForm((f) => ({ ...f, category: v as typeof f.category }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NO_SHOW">No-show</SelectItem>
                  <SelectItem value="PRICE_GOUGING">Prix abusif</SelectItem>
                  <SelectItem value="QUALITY">Qualité</SelectItem>
                  <SelectItem value="BEHAVIOR">Comportement</SelectItem>
                  <SelectItem value="SPAM">Spam</SelectItem>
                  <SelectItem value="OTHER">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Sévérité</Label>
              <Select
                value={quickReportForm.severity}
                onValueChange={(v) =>
                  setQuickReportForm((f) => ({ ...f, severity: v as typeof f.severity }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Faible</SelectItem>
                  <SelectItem value="MEDIUM">Moyenne</SelectItem>
                  <SelectItem value="HIGH">Élevée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="qr-desc">Description</Label>
              <Textarea
                id="qr-desc"
                placeholder="Décrivez le problème..."
                value={quickReportForm.description}
                onChange={(e) =>
                  setQuickReportForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setQuickReportOpen(false)}
              disabled={createQuickReportMutation.isLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!selectedProjectId) return;
                createQuickReportMutation.mutate({
                  privateProjectId: selectedProjectId,
                  companyId: quickReportForm.companyId || undefined,
                  category: quickReportForm.category,
                  severity: quickReportForm.severity,
                  description: quickReportForm.description.trim(),
                });
              }}
              disabled={
                createQuickReportMutation.isLoading ||
                quickReportForm.description.trim().length < 3
              }
            >
              {createQuickReportMutation.isLoading ? "En cours..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
